import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import lfApi from "@lf/api-client/lfApi";
import { getBillingPortalUrl, switchSubscriptionTier } from "../../api/lfApiSubscription";
import { LoadingButton } from '@lf/ui-primitives';
import { track } from '@lf/analytics-events';
import MySharedPlans from './MySharedPlans';

const PLAN_INFO = {
    monthly_standard: { label: 'Student Plan', price: '$3.99/mo', isFamily: false },
    monthly_family: { label: 'Family Plan', price: '$7.99/mo', isFamily: true },
    monthly: { label: 'Student Plan', price: '$3.99/mo', isFamily: false },
};

function getCurrentPlanInfo(subscriptionAction) {
    if (!subscriptionAction || subscriptionAction.action !== 'none') return null;
    return PLAN_INFO[subscriptionAction.price_type] || null;
}

async function saveProfile(displayName) {
    try {
        const response = await lfApi.put('/user/profile', { display_name: displayName });
        if (response.status === 200) {
            return response.data;
        }
        console.error('Failed to save profile', response.data);
    } catch (error) {
        console.error('Error saving profile', error);
    }
    return null;
}


function Settings({ userProfile, setUserProfile, logout, subscriptionAction }) {
    const [billingPortalUrl, setBillingPortalUrl] = useState(null);
    const [isUpgrading, setIsUpgrading] = useState(false);

    const [displayName, setDisplayName] = useState(userProfile.name);
    const [profileEditError, setProfileEditError] = useState(null);

    
    const location = useLocation();

    useEffect(() => {
        getBillingPortalUrl().then(setBillingPortalUrl);
    }, []);

    const navigate = useNavigate();

    const navigateToSettings = () => {
        navigate('');
    };

    const navigateToProfile = () => {
        setDisplayName(userProfile.name);
        setProfileEditError(null);

        navigate('profile');
    };

    const navigateToShares = () => {
        navigate('shares');
    };


    const onSaveProfile = async () => {
        const result = await saveProfile(displayName);

        if (result) {
            setUserProfile(result);
            navigateToSettings();
        } else {
            setProfileEditError('Failed to save profile. Please try again later.');
        }
    };


    const onLogout = async () => {
        await lfApi.post('/auth/logout', { redirect: false });

        logout();
        navigate('/');
    };

    const onUpgradeToFamily = async () => {
        if (isUpgrading) return;
        setIsUpgrading(true);
        track('upgrade_cta_click', {
            from_plan: 'student',
            to_plan: 'family',
            surface: 'settings',
        });
        const res = await switchSubscriptionTier('monthly_family');
        if (res && res.success) {
            // Reload so the dashboard re-fetches the subscription action and the
            // Settings card reflects the new Family plan.
            window.location.reload();
        } else {
            setIsUpgrading(false);
        }
    };

    return <div className="w-full text-left">
        <div className="flex w-full justify-between mb-6">
            <div>
                {location.pathname === '/setting' && (
                    <button 
                        className="text-gray-500 hover:underline py-2" 
                        onClick={() => navigate('..')}>
                        ← Back
                    </button>
                )}
            </div>
            <button className="bg-blue-500 text-white py-2 px-4 rounded mr-2" 
                    onClick={onLogout}>
                Logout
            </button>
        </div>

        <Routes>
            <Route path="" element={
                <div className="mb-12">
                    <h1 className="text-xl font-semibold">Profile</h1>

                    <table className="">
                        <tbody>
                            <tr>
                                <td className="p-1 pl-0">Display Name:</td>
                                <td className="p-1">{userProfile.name}</td>
                            </tr>
                            <tr>
                                <td className="p-1 pl-0">Email:</td>
                                <td className="p-1">{userProfile.email}</td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="mt-4">
                        <button className="bg-blue-500 text-white py-2 px-4 rounded mr-2" 
                                onClick={navigateToProfile}>
                            Edit profile
                        </button>
                    </div>

                    {
                        billingPortalUrl && (
                            <div>
                                <h1 className="text-xl font-semibold mt-6 mb-4">Subscription</h1>
                                {(() => {
                                    const planInfo = getCurrentPlanInfo(subscriptionAction);
                                    return planInfo ? (
                                        <div className="mb-4">
                                            <div className="text-sm text-gray-600">
                                                <span className="font-medium">{planInfo.label}</span> — {planInfo.price}
                                            </div>
                                            {!planInfo.isFamily && (
                                                <div className="mt-3">
                                                    <button
                                                        type="button"
                                                        disabled={isUpgrading}
                                                        onClick={onUpgradeToFamily}
                                                        className="bg-primary-600 text-white py-2 px-4 rounded mr-2 inline-block disabled:opacity-60"
                                                        onMouseEnter={() => track('upgrade_cta_view', {
                                                            current_plan: 'student',
                                                            surface: 'settings',
                                                        })}
                                                    >
                                                        {isUpgrading ? 'Upgrading…' : 'Upgrade to Family Plan'}
                                                    </button>
                                                    <div className="text-xs text-gray-500 mt-1">$7.99/mo — up to 4 students</div>
                                                </div>
                                            )}
                                        </div>
                                    ) : null;
                                })()}
                                <a href={billingPortalUrl}
                                    target="_blank" rel="noopener noreferrer"
                                    className="bg-blue-500 text-white py-2 px-4 rounded cursor-pointer"
                                    onClick={() => track('billing_portal_click', {
                                        surface: 'settings',
                                        current_plan: subscriptionAction?.price_type || '',
                                    })}>
                                    Manage subscription
                                </a>
                            </div>
                        )
                    }

                    <h1 className="text-xl font-semibold mt-6 mb-4">Shared study plans</h1>
                    <button
                        className="bg-blue-500 text-white py-2 px-4 rounded"
                        onClick={navigateToShares}>
                        View my shared study plans
                    </button>
                </div>
            } />

            <Route path="shares" element={<MySharedPlans />} />

            <Route path="profile" element={
                <div>
                    <h1 className="text-xl font-semibold">Edit profile</h1>

                    <table className="mt-4">
                    <tbody>
                        <tr>
                            <td className="p-1 pl-0">Display Name:</td>
                            <td className="p-1">
                                <input className="p-1 border border-gray-300" type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
                            </td>
                            </tr>
                        </tbody>
                    </table>

                    {
                        profileEditError && (
                            <div className="text-red-500 my-2">
                                {profileEditError}
                            </div>
                        )
                    }

                    <div className="mt-4 flex">
                        <LoadingButton text="Save" onClick={onSaveProfile} />
                        <button className="bg-blue-500 text-white py-2 px-4 rounded ml-2" 
                                onClick={navigateToSettings}>
                            Cancel
                        </button>
                    </div>
                </div>
            } />

        </Routes>
    </div>;
};

export default Settings;
